import { Injectable } from '@angular/core';
import { KeyValue } from '@angular/common';
import { environment } from 'src/environments/environment';
import { UserNavigation } from '../models/user-navigation.model';
import { NavigationItem } from '../models/navigation-item.model';
import { NavigationPath } from '../models/navigation-path.model';
import { PathFile } from '../models/path-file.model';
import { PathQuery } from '../models/path-query.model';
import { QueryParameter } from '../models/query-parameter.model';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { NavigationLevelEnum } from '../models/navigation-level.enum';
import { UserNavToMenuItemSubConverter } from '../converters/usernav-to-munuitem-sub.converter';
import { PermIdIndexerEnum } from '../models/permid-indexer-enum';
import { NavigationUtils } from '../navigation-utils';
import { NavigationMenuTypeEnum } from '../models/navigation-menu-type-enum';

@Injectable({
    providedIn: 'root'
})

export class UserNavigationMappingService {
    private _maxNavigationId = Number.MAX_SAFE_INTEGER - 10000;

    private _webserver: string;

    get Webserver(): string {
        return this._webserver;
    }
    
    set Webserver(value: string) {
        this._webserver = value;
    }

    private _callback: (menuEvent: MenuItemCommandEvent) => void;
    
    get MenuItemCallback(): (menuEvent: MenuItemCommandEvent) => void {
        return this._callback;
    }

    set MenuItemCallback(value: (menuEvent: MenuItemCommandEvent) => void) {
        this._callback = value;
    }

    public mapUserNavigation(nav: any): UserNavigation {
        const navItems: Array<NavigationItem> = this.mapNavigationItems(nav);
        const userNav = new UserNavigation(navItems);
        return userNav;
    }

    public getAvailableSubscriptions(nav: UserNavigation): Array<KeyValue<string, string>> {
        const subs = new Array<KeyValue<string, string>>();
        if (nav && nav.Items) {
            nav.Items.forEach(ni => {
                const isSubscriptionItem = (ni.PermItemID !== environment.HomePermId) 
                                         && (ni.PermItemID !== environment.SystemReportsPermId) 
                                         && (ni.PermItemID !== environment.RequestFacilityAccess)
                                         && (ni.PermItemID !== environment.UserProfilePage);
                if (isSubscriptionItem) {
                    const kv: KeyValue<string, string> = { key: ni.PermItemID, value: ni.ItemName };
                    subs.push(kv);
                }
            });
        }
        return subs;
    }

    public getAvailableSubscriptionPermIds(nav: any): Array<string> {
        const pairs = this.getAvailableSubscriptions(nav);
        const subs = new Array<string>();
        pairs.forEach(pair => {
            subs.push(pair.key);            
        });
        return subs;
    }

    public mapSubscriptionIdToPermId(nav: any, selectedSubId: number): string {
        const key = `_p${selectedSubId}`;
        const pairs = this.getAvailableSubscriptions(nav);
        const match = pairs.find(k => k.key.includes(key));
        if (match && (match !== undefined)) {
            return match.key;
        }
        return '0';
    }

    // public mapPermIdToSubscriptionId(nav: any, permId: string): number {
    //     const parts = permId.split('_p');
    // }

    private truncateNavHospitals(userNavigation: NavigationItem, hospid: string, truncateChildUnits: boolean) {
        userNavigation.Items.forEach(ni => {
            const isSubscriptionItem = (ni.PermItemID !== environment.HomePermId) 
                                     && (ni.PermItemID !== environment.SystemReportsPermId) 
                                     && (ni.PermItemID !== environment.RequestFacilityAccess)
                                     && (ni.PermItemID !== environment.UserProfilePage);
            if (isSubscriptionItem)  {
                ni.Items.forEach(hi => {
                    this.truncateItems(hi, hospid, truncateChildUnits)
                });
            }
        });
    }

    private truncateItems(ni: NavigationItem, hospid: string, truncateChildUnits: boolean) {
        if (hospid === '') {
            ni.Items = new Array<NavigationItem>();
        }

        ni.Items.forEach(child => {

            const fid = NavigationUtils.GetHospitalIdNavItem(child);
            if (fid !== '') {
                if (hospid !== fid) {
                    child.Items = new Array<NavigationItem>();
                } else {
                    if (truncateChildUnits) {
                        child.Items.forEach(ui => {
                            const uid = NavigationUtils.GetUnitIdNavItem(child);
                            if (uid !== '') {
                                ui.Items = new Array<NavigationItem>();
                            }
                        });
                    }
                }
            }
        });
    }

    private truncateNavUnits(userNavigation: any, unitid: string) {
        userNavigation.items.forEach(si => {
            const isSubscriptionItem = (si.PermItemID !== environment.HomePermId) 
                                     && (si.PermItemID !== environment.SystemReportsPermId) 
                                     && (si.PermItemID !== environment.RequestFacilityAccess)
                                     && (si.PermItemID !== environment.UserProfilePage);
            if (isSubscriptionItem)  {
                si.items.forEach(hi => {
                    const fid = NavigationUtils.GetHospitalIdNavItem(hi);
                    if (fid !== '') {
                        hi.items.forEach(ui => {
                                const uid = NavigationUtils.GetUnitIdNavItem(ui);
                                if (unitid !== uid) {
                                    ui.items = new Array<NavigationItem>();
                                }
                        });
                    }
                });
            }
        });
    }

    public truncateNavMenu(userNavigation: any, activeLinkId: string): UserNavigation {
        
        const hospid = NavigationUtils.GetHospitalIdFromPermId(activeLinkId);
        const unitid = NavigationUtils.GetUnitIdFromPermId(activeLinkId);
        const linkIncludesHospId = (hospid !== '');
        const linkIncludesUnitId = (unitid !== '');
        if ((!linkIncludesHospId) && (!linkIncludesUnitId)) {
            this.truncateNavHospitals(userNavigation, '', true);
        } else if ((linkIncludesHospId) && (!linkIncludesUnitId)) {
            this.truncateNavHospitals(userNavigation, hospid, true);
        } else if ((linkIncludesHospId) && (linkIncludesUnitId)) {
            this.truncateNavHospitals(userNavigation, hospid, false);
            this.truncateNavUnits(userNavigation, unitid);
        }

        return userNavigation;
    }

    public getTruncatedMenuItems(menuType: NavigationMenuTypeEnum, userNavigation: UserNavigation, activeLinkId: string, hospMenuItems: Map<string, Array<MenuItem>>, unitMenuItems: Map<string, Array<MenuItem>>): MenuItem[] {
        const navMenuTruncated = this.truncateNavMenu(userNavigation, activeLinkId);
        // ensure indicator leaf retains a stub child so breadcrumbs recognize it
        if (NavigationUtils.IsLastPartIndicatorItemFromPermId(activeLinkId)) {
            const leaf = navMenuTruncated.getNavigationItemUsingPermID(activeLinkId);
            if (leaf && (!leaf.Items || leaf.Items.length === 0)) {
                const stub = new NavigationItem(0, `${leaf.PermItemID}-stubind`, ' ', leaf.ItemID, 0, 0, null, []);
                leaf.Items = [stub];
            }
        }
        const menuItemsTruncated = this.getMenuItems(menuType, navMenuTruncated, activeLinkId, true, hospMenuItems, unitMenuItems);
        return menuItemsTruncated;
    }

    public getMenuItems(menuType: NavigationMenuTypeEnum, userNavigation: UserNavigation, activeLinkId: string, addStubs: boolean, hospMenuItems: Map<string, Array<MenuItem>>, unitMenuItems: Map<string, Array<MenuItem>>): MenuItem[] {
        const homeid = environment.HomePermId;
        const subscriptionPermIds: Array<string> = this.getAvailableSubscriptionPermIds(userNavigation);
        const menuItems = UserNavToMenuItemSubConverter.convertToMenuItems(menuType, userNavigation, activeLinkId, this.MenuItemCallback, addStubs, environment.MaxNumberOfHospitals, environment.MaxNumberOfUnits, homeid, subscriptionPermIds, hospMenuItems, unitMenuItems);
        return menuItems;
    }

    public getMenuItemsLevel(permItemID: string, userNavigation: UserNavigation, menu: any[]): NavigationLevelEnum {
        let menuLevel: NavigationLevelEnum = NavigationLevelEnum.NotSet;
        if (permItemID) {
            if (permItemID === environment.HomePermId) {
                menuLevel = NavigationLevelEnum.Home;
            } else {
                const siblings = this.getMenuItemSiblings(permItemID, menu);
                const stub = this.findStub(siblings);
                if (stub) {
                    menuLevel = stub.id.includes('hosp') ? NavigationLevelEnum.Hospital : NavigationLevelEnum.Unit;
                } else {
                    const hierarchy: NavigationItem[] = userNavigation.getNavigationItemHierarchyUsingPermID(permItemID);
                    menuLevel = (hierarchy) ? (hierarchy.length) : 1;
                }
            }
        }
        return menuLevel;
    }

    private findStub(siblings: Array<MenuItem>): MenuItem {
        const stub = siblings.find(i => (i.id.indexOf('stub') > 0));
        return stub;
    }

    private getMenuItemSiblings(menuItemId: string, menu: any[]): MenuItem[] {
        const siblings = this.getSubMenuItemSiblings(menuItemId, menu, null);
        return siblings;
    }

    private getSubMenuItemSiblings(menuItemId: string, subMenuItems: Array<any>, parent: any): MenuItem[] {
        let mi: MenuItem;
        let siblings = new Array<MenuItem>();
        // tslint:disable-next-line:prefer-for-of
        for (let index = 0; index < subMenuItems.length; index++) {
            const item = subMenuItems[index];
            if (item.id === menuItemId) {
                mi = item;
                siblings = subMenuItems;
                break;
            }
            if (!mi && item.items && (siblings.length < 1)) {
                siblings = this.getSubMenuItemSiblings(menuItemId, item.items, item);
            }
        }
        return siblings;
    }

    private mapNavigationItems(nav: any): NavigationItem[] {
        const navItems = new Array<NavigationItem>();
        if (nav && nav.is) {
            nav.is.forEach(item => {
                const navItem = this.mapNavigationItem(item, nav.id);
                navItems.push(navItem);
            });
        }
        return navItems;
    }

    public mapNavigationItem(item: any, parentId: number): NavigationItem {
        const path = this.mapNavigationPath(item);
        const subitems = this.mapNavigationItems(item);

        // Check to see if the Indicator need stub child items adding
        const parts = item.pid.split('_');
        if (parts && parts.length === (PermIdIndexerEnum.Indicator + 1)) {
            const indicatorId = parts[PermIdIndexerEnum.Indicator];
            const isHospitalInPath = item.pid.includes('_f');
            if (isHospitalInPath && indicatorId && indicatorId.startsWith('a')) {
                if (subitems.length === 0) {
                    const documentsPermId = item.pid + environment.IndicatorDocumentSuffix; 
                    this._maxNavigationId++;
                    const docNavItem = new NavigationItem(this._maxNavigationId, documentsPermId, 'Documents', item.id, item.uc, item.ic, null, new Array<NavigationItem>());
                    subitems.push(docNavItem);
                    const tutorialPermId = item.pid + environment.IndicatorTutorialSuffix; 
                    this._maxNavigationId++;
                    const tutNavItem = new NavigationItem(this._maxNavigationId, tutorialPermId, 'Tutorials', item.id, item.uc, item.ic, null, new Array<NavigationItem>());
                    subitems.push(tutNavItem);
                    const overviewPermId = item.pid + environment.IndicatorOverviewSuffix; 
                    this._maxNavigationId++;
                    const overNavItem = new NavigationItem(this._maxNavigationId, overviewPermId, 'General Overview Tutorial', item.id, item.uc, item.ic, path, new Array<NavigationItem>());
                    subitems.push(overNavItem);
                }
            }
        }

        const navItem = new NavigationItem(item.id, item.pid, item.in, parentId, item.uc, item.ic, path, subitems);
        return navItem;
    }

    public mapNavigationPath(item: any): NavigationPath {
        let navPath: NavigationPath;
        const path = item.pth;
        if (path) {
            const isExternalLink = (path.includes('http://')) || (path.includes('https://'));
            if (isExternalLink === true) {
                const parts = path.split('/');
                const webserver = `${parts[0]}//${parts[2]}`;
                const pathQuery = this.mapPathQuery(path);
                let query = '';
                if (pathQuery) {
                    query = pathQuery.toString();
                }

                const dir: string = path;
                const minusWeb = dir.replace(webserver, '');
                const minusQuery = minusWeb.replace(query, '');
                const dirParts = minusQuery.split('/');
                const file = dirParts[dirParts.length - 1];
                const fileParts = file.split('.');

                let pathFile: PathFile;
                let directory = '';
                // check for a directory path but no file name
                if (fileParts.length === 1) {
                    directory = minusQuery;
                } else {
                    directory = minusQuery.replace(file, '');
                    const fn = fileParts[0];
                    const ext = fileParts[1];
                    const exttrim = ext.replace('?', '');
                    pathFile = new PathFile(fn, exttrim);
                }
                if (directory.startsWith('/')) {
                    directory = directory.substring(1, directory.length);
                }
                if (directory.endsWith('/')) {
                    directory = directory.substr(0, directory.length - 1);
                }
                navPath = new NavigationPath(webserver, directory, pathFile, pathQuery);
            } else {
                const pathFile = this.mapPathFile(path);
                const pathQuery = this.mapPathQuery(path);
                const directory = this.mapDirectory(path, pathFile, pathQuery);
                navPath = new NavigationPath(this.Webserver, directory, pathFile, pathQuery);
            }
        }
        return navPath;
    }

    private mapDirectory(path: string, file: PathFile, query: PathQuery): string {
        let dir: string;
        if (path) {
            if (file) {
                const lastSlash = path.lastIndexOf('/');
                path = path.substring(0, lastSlash + 1);
            } else if (query) {
                if (path.includes('?')) {
                    const question = path.indexOf('?');
                    path = path.substring(0, question);
                }
                if (path.includes('.')) {
                    const lastSlash = path.lastIndexOf('/');
                    path = path.substring(0, lastSlash + 1);
                }
            }
            dir = path.substring(2, path.length);
        }
        return dir;
    }


    private mapPathFile(path: string): PathFile {
        let pathFile: PathFile;
        if (path) {
            let uri = path;
            const query = path.indexOf('?');
            if (query > 0) {
                uri = path.substr(0, query);
            }

            const pathParts = uri.split('.');
            if (pathParts.length > 1) {
                const directory = pathParts[0];
                const ext = pathParts[1];
                let filename = directory;
                const dirParts = directory.split('/');
                if ((dirParts) && (dirParts.length > 0)) {
                    filename = dirParts[dirParts.length - 1];
                }
                pathFile = new PathFile(filename, ext);
            }
        }
        return pathFile;
    }

    private mapPathQuery(path: any): PathQuery {
        let pathQuery: PathQuery;
        if (path) {
            const pathParts = path.split('?');
            if (pathParts.length > 1) {
                const queryString = pathParts[pathParts.length - 1];
                if (queryString) {
                    const paramPairs = queryString.split('&');
                    const params = this.mapQueryParameters(paramPairs);
                    pathQuery = new PathQuery(params);
                }
            }
        }
        return pathQuery;
    }

    private mapQueryParameters(query: Array<string>): QueryParameter[] {
        const params = new Array<QueryParameter>();
        if (query && (query.length > 0)) {
            query.forEach(qp => {
                const queryParts = qp.split('=');
                const param = new QueryParameter(queryParts[0], queryParts[1]);
                params.push(param);
            });
        }
        return params;
    }

    // Clone
    public cloneUserNavigation(nav: any): UserNavigation {
        const navItems: Array<NavigationItem> = this.cloneNavigationItems(nav);
        const userNav = new UserNavigation(navItems);
        return userNav;
    }

    private cloneNavigationItems(nav: any): NavigationItem[] {
        const navItems = new Array<NavigationItem>();
        if (nav && nav.items) {
            nav.items.forEach(item => {
                const navItem = this.cloneNavigationItem(item, nav.itemID);
                navItems.push(navItem);
            });
        }
        return navItems;
    }

    private cloneNavigationItem(item: any, parentId: number): NavigationItem {
        const path = this.cloneNavigationPath(item);
        const subitems = this.cloneNavigationItems(item);
        const navItem = new NavigationItem(item.itemID, item.permItemID, item.itemName, parentId, item.unitCount, item.indicatorCount, path, subitems);
        return navItem;
    }

    public cloneNavigationPath(item: any): NavigationPath {
        let navPath: NavigationPath;
        const path = item.path;
        if (path) {
            const pathFile = this.clonePathFile(path);
            const pathQuery = this.clonePathQuery(path);
            navPath = new NavigationPath(path.webserver, path.directory, pathFile, pathQuery);
        }
        return navPath;
    }

    private clonePathFile(path: any): PathFile {
        let pathFile: PathFile;
        if (path && path.file) {
            const file = path.file;
            pathFile = new PathFile(file.filename, file.extension);
        }
        return pathFile;
    }

    private clonePathQuery(path: any): PathQuery {
        let pathQuery: PathQuery;
        if (path && path.query) {
            const query = path.query;
            const params = this.cloneQueryParameters(query);
            pathQuery = new PathQuery(params);
        }
        return pathQuery;
    }

    private cloneQueryParameters(query: any): QueryParameter[] {
        const params = new Array<QueryParameter>();
        if (query && query.queryParameters) {
            query.queryParameters.forEach(queryParam => {
                const param = new QueryParameter(queryParam.param, queryParam.value);
                params.push(param);
            });
        }
        return params;
    }
}
